import React, { useEffect, useState } from "react";
import {
  Button, Card, Col, Form, Input, InputNumber, Modal, Popconfirm,
  Row, Space, Table, Tag, Typography, Alert, Spin,
} from "antd";
import { PlusOutlined, DeleteOutlined, BulbOutlined } from "@ant-design/icons";
import axios from "axios";

const { Title, Text } = Typography;
const API_URL = process.env.REACT_APP_API_URL;

const TIPOS_ACTIVIDAD = [
  "Preparación del terreno",
  "Siembra", "Fertilización", "Riego", "Poda", "ControlPlagas",
  "Fumigación", "Cosecha", "Aporque", "Otro",
];

const TIPO_COLOR = {
  "Preparación del terreno": "#8B4513",
  Siembra: "green", Fertilización: "blue", Riego: "cyan", Poda: "orange",
  ControlPlagas: "red", Fumigación: "volcano", Cosecha: "gold", Aporque: "lime", Otro: "default",
};

export default function FrmTipoCultivo() {
  const [tipos, setTipos] = useState([]);
  const [cargando, setCargando] = useState(false);
  const [alerta, setAlerta] = useState(null);

  // Modal nuevo tipo
  const [modalAbierto, setModalAbierto] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [form] = Form.useForm();
  const [actividades, setActividades] = useState([
    { _key: 1, tipoActividad: "Siembra", diasDespieSiembra: 0, descripcion: "", orden: 1 },
  ]);

  const cargar = async () => {
    try {
      setCargando(true);
      const resp = await axios.get(`${API_URL}/TipoCultivo`);
      const lista = Array.isArray(resp.data?.$values) ? resp.data.$values
        : Array.isArray(resp.data) ? resp.data : [];
      setTipos(lista);
    } catch {
      setAlerta({ tipo: "error", texto: "Error al cargar los tipos de cultivo." });
    } finally {
      setCargando(false);
    }
  };

  useEffect(() => { cargar(); }, []);

  const agregarActividad = () => {
    const orden = actividades.length + 1;
    setActividades(prev => [
      ...prev,
      { _key: Date.now(), tipoActividad: "Fertilización", diasDespieSiembra: 0, descripcion: "", orden },
    ]);
  };

  const actualizarActividad = (key, campo, valor) => {
    setActividades(prev => prev.map(a => a._key === key ? { ...a, [campo]: valor } : a));
  };

  const eliminarActividad = (key) => {
    setActividades(prev => prev.filter(a => a._key !== key).map((a, i) => ({ ...a, orden: i + 1 })));
  };

  const abrirModal = () => {
    form.resetFields();
    setActividades([
      { _key: 1, tipoActividad: "Siembra", diasDespieSiembra: 0, descripcion: "", orden: 1 },
    ]);
    setModalAbierto(true);
  };

  const guardar = async () => {
    try {
      const values = await form.validateFields();
      setGuardando(true);
      const body = {
        nombre: values.nombre,
        descripcion: values.descripcion ?? null,
        diasEstimadosCosecha: values.diasEstimadosCosecha ?? null,
        actividades: actividades.map((a, i) => ({
          tipoActividad: a.tipoActividad,
          diasDespieSiembra: a.diasDespieSiembra ?? 0,
          descripcion: a.descripcion || null,
          orden: i + 1,
        })),
      };
      await axios.post(`${API_URL}/TipoCultivo`, body);
      setModalAbierto(false);
      await cargar();
      setAlerta({ tipo: "success", texto: "✅ Tipo de cultivo creado correctamente." });
    } catch (err) {
      if (err?.errorFields) return;
      const msg = err?.response?.data?.mensaje ?? err?.response?.data?.detalle ?? err?.message ?? "Error desconocido";
      setAlerta({ tipo: "error", texto: `❌ ${msg}` });
    } finally {
      setGuardando(false);
    }
  };

  const eliminarTipo = async (id) => {
    try {
      await axios.delete(`${API_URL}/TipoCultivo/${id}`);
      await cargar();
    } catch {
      setAlerta({ tipo: "error", texto: "Error al eliminar." });
    }
  };

  const columnas = [
    {
      title: "Tipo de cultivo",
      key: "nombre",
      render: (_, r) => (
        <div>
          <Text strong style={{ fontSize: 15 }}>🌱 {r.nombre}</Text>
          {r.descripcion && (
            <div><Text type="secondary" style={{ fontSize: 12 }}>{r.descripcion}</Text></div>
          )}
        </div>
      ),
    },
    {
      title: "Días est. cosecha",
      dataIndex: "diasEstimadosCosecha",
      width: 160,
      align: "center",
      render: v => v ? <Tag color="green">{v} días</Tag> : <Text type="secondary">—</Text>,
    },
    {
      title: "Actividades programadas",
      key: "actividades",
      render: (_, r) => {
        const acts = Array.isArray(r.actividades?.$values) ? r.actividades.$values
          : Array.isArray(r.actividades) ? r.actividades : [];
        return (
          <Space wrap size={4}>
            {acts.map(a => (
              <Tag key={a.id} color={TIPO_COLOR[a.tipoActividad] ?? "default"}>
                {a.tipoActividad} (+{a.diasDespieSiembra}d)
              </Tag>
            ))}
            {acts.length === 0 && <Text type="secondary">Sin actividades</Text>}
          </Space>
        );
      },
    },
    {
      title: "",
      key: "acciones",
      width: 60,
      align: "center",
      render: (_, r) => (
        <Popconfirm
          title="¿Eliminar este tipo de cultivo?"
          description="No se eliminarán los cultivos ya creados con este tipo."
          onConfirm={() => eliminarTipo(r.id)}
          okText="Sí" cancelText="No" okButtonProps={{ danger: true }}
        >
          <Button size="small" danger icon={<DeleteOutlined />} />
        </Popconfirm>
      ),
    },
  ];

  return (
    <div style={{ padding: 16 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
        <div>
          <Title level={3} style={{ margin: 0 }}>🌿 Tipos de cultivo</Title>
          <Text type="secondary">
            Define qué cultivos maneja la finca y sus actividades sugeridas.
            Al crear un cultivo nuevo, el sistema genera el calendario automáticamente.
          </Text>
        </div>
        <Button type="primary" icon={<PlusOutlined />} onClick={abrirModal}>
          Nuevo tipo
        </Button>
      </div>

      {alerta && (
        <Alert type={alerta.tipo} message={alerta.texto} showIcon closable
          onClose={() => setAlerta(null)} style={{ marginBottom: 12 }} />
      )}

      <Spin spinning={cargando}>
        <Table
          rowKey="id"
          columns={columnas}
          dataSource={tipos}
          pagination={false}
          size="middle"
          locale={{ emptyText: "Sin tipos de cultivo. Ejecuta el script SQL o crea uno nuevo." }}
        />
      </Spin>

      {/* ══ Modal nuevo tipo ══ */}
      <Modal
        title="➕ Nuevo tipo de cultivo"
        open={modalAbierto}
        onCancel={() => setModalAbierto(false)}
        onOk={guardar}
        okText="Guardar"
        confirmLoading={guardando}
        width={700}
      >
        <Form form={form} layout="vertical">
          <Row gutter={16}>
            <Col xs={24} sm={16}>
              <Form.Item label="Nombre del cultivo" name="nombre"
                rules={[{ required: true, message: "Ingrese el nombre." }]}>
                <Input placeholder="Ej: Maíz, Papa, Tomate, Café..." />
              </Form.Item>
            </Col>
            <Col xs={24} sm={8}>
              <Form.Item label="Días estimados a cosecha" name="diasEstimadosCosecha">
                <InputNumber min={1} max={3650} style={{ width: "100%" }}
                  placeholder="Ej: 120" />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item label="Descripción (opcional)" name="descripcion">
            <Input placeholder="Ej: Cultivo de maíz tecnificado en clima cálido" />
          </Form.Item>
        </Form>

        <div style={{ marginBottom: 8 }}>
          <Text strong>
            <BulbOutlined style={{ marginRight: 4, color: "#fa8c16" }} />
            Actividades sugeridas (se crean automáticamente al registrar un cultivo de este tipo)
          </Text>
        </div>

        <div style={{ background: "#fafafa", padding: 12, borderRadius: 8, marginBottom: 8 }}>
          <Row gutter={8} style={{ marginBottom: 6 }}>
            <Col xs={6}><Text type="secondary" style={{ fontSize: 12 }}>Actividad</Text></Col>
            <Col xs={5}><Text type="secondary" style={{ fontSize: 12 }}>Días desde siembra</Text></Col>
            <Col xs={11}><Text type="secondary" style={{ fontSize: 12 }}>Descripción</Text></Col>
            <Col xs={2} />
          </Row>

          {actividades.map((act, idx) => (
            <Row key={act._key} gutter={8} style={{ marginBottom: 8 }} align="middle">
              <Col xs={6}>
                <select
                  style={{ width: "100%", height: 32, borderRadius: 6,
                    border: "1px solid #d9d9d9", padding: "0 8px", fontSize: 13 }}
                  value={act.tipoActividad}
                  onChange={e => actualizarActividad(act._key, "tipoActividad", e.target.value)}
                >
                  {TIPOS_ACTIVIDAD.map(t => (
                    <option key={t} value={t}>{t}</option>
                  ))}
                </select>
              </Col>
              <Col xs={5}>
                <InputNumber
                  min={0} max={3650}
                  style={{ width: "100%" }}
                  value={act.diasDespieSiembra}
                  onChange={v => actualizarActividad(act._key, "diasDespieSiembra", v ?? 0)}
                  addonAfter="días"
                />
              </Col>
              <Col xs={11}>
                <Input
                  placeholder="Descripción opcional..."
                  value={act.descripcion}
                  onChange={e => actualizarActividad(act._key, "descripcion", e.target.value)}
                />
              </Col>
              <Col xs={2} style={{ textAlign: "center" }}>
                {actividades.length > 1 && (
                  <Button size="small" danger
                    onClick={() => eliminarActividad(act._key)}>✕</Button>
                )}
              </Col>
            </Row>
          ))}

          <Button type="dashed" size="small" onClick={agregarActividad} block>
            + Agregar actividad
          </Button>
        </div>

        <Text type="secondary" style={{ fontSize: 12 }}>
          💡 Puedes dejar sin actividades y agregarlas manualmente en cada cultivo.
        </Text>
      </Modal>
    </div>
  );
}
